import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ShoppingCart from '@mui/icons-material/ShoppingCart';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import Check from '@mui/icons-material/Check';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Wallet from '@mui/icons-material/Wallet';
import Search from '@mui/icons-material/Search';
import FilterList from '@mui/icons-material/FilterList';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useData } from '../../contexts/DataContext';
import { Fee, PricingTier } from '../../types';

interface TicketQuantities {
  student: number;
  adult: number;
  senior: number;
}

type DateFilter = 'all' | 'thisWeek' | 'nextWeek' | 'thisMonth' | 'nextMonth';
type SportFilter = 'all' | 'basketball' | 'football' | 'baseball' | 'swimming' | 'other';

const getDateRange = (filter: DateFilter): { start: Date; end: Date } | null => {
  const now = new Date();
  const dayOfWeek = now.getDay();

  switch (filter) {
    case 'thisWeek': {
      const start = new Date(now);
      start.setDate(now.getDate() - dayOfWeek);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'nextWeek': {
      const start = new Date(now);
      start.setDate(now.getDate() - dayOfWeek + 7);
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    case 'thisMonth': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      return { start, end };
    }
    case 'nextMonth': {
      const start = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 2, 0, 23, 59, 59, 999);
      return { start, end };
    }
    default:
      return null;
  }
};

const getSportType = (eventName: string): SportFilter => {
  const name = eventName.toLowerCase();
  if (name.includes('basketball')) return 'basketball';
  if (name.includes('football')) return 'football';
  if (name.includes('baseball')) return 'baseball';
  if (name.includes('swimming')) return 'swimming';
  return 'other';
};

export const EventsPage: React.FC = () => {
  const { isAuthenticated, student } = useAuth();
  const { addToCart, cart } = useCart();
  const { fees: mockFees, schoolYears: mockSchoolYears } = useData();
  const [quantities, setQuantities] = useState<Map<string, TicketQuantities>>(new Map());
  const [loadingItems, setLoadingItems] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>('thisWeek');
  const [sportFilter, setSportFilter] = useState<SportFilter>('all');

  const pricingTier: PricingTier = student?.pricingTier || 'full';

  const eventFees = useMemo(() => {
    let filtered = mockFees.filter(fee =>
      fee.isActive &&
      fee.categoryId === '5' &&
      !fee.course
    );

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(fee =>
        fee.name.toLowerCase().includes(query) ||
        fee.description.toLowerCase().includes(query)
      );
    }

    if (dateFilter !== 'all') {
      const dateRange = getDateRange(dateFilter);
      if (dateRange) {
        filtered = filtered.filter(fee => {
          const availableFrom = new Date(fee.availableFrom);
          const availableTo = new Date(fee.availableTo);
          return (availableFrom <= dateRange.end && availableTo >= dateRange.start);
        });
      }
    }

    if (sportFilter !== 'all') {
      filtered = filtered.filter(fee => getSportType(fee.name) === sportFilter);
    }

    filtered.sort((a, b) => new Date(a.availableFrom).getTime() - new Date(b.availableFrom).getTime());

    return filtered;
  }, [searchQuery, dateFilter, sportFilter]);

  const getSchoolYearName = (schoolYearId: string) => {
    const sy = mockSchoolYears.find(s => s.id === schoolYearId);
    return sy?.name || '';
  };

  const getTicketQuantities = (feeId: string): TicketQuantities => {
    return quantities.get(feeId) || { student: 0, adult: 0, senior: 0 };
  };

  const updateTicketQuantity = (feeId: string, type: keyof TicketQuantities, value: number) => {
    const current = getTicketQuantities(feeId);
    const newQuantities = new Map(quantities);
    newQuantities.set(feeId, { ...current, [type]: Math.max(0, value) });
    setQuantities(newQuantities);
  };

  const getTotalQuantity = (feeId: string): number => {
    const q = getTicketQuantities(feeId);
    return q.student + q.adult + q.senior;
  };

  const getPrice = (fee: Fee, ticketType: 'student' | 'adult' | 'senior'): number => {
    if (ticketType === 'student') {
      switch (pricingTier) {
        case 'full':
          return fee.basePrice * 0.75;
        case 'reduced':
          return fee.reducedPrice * 0.75;
        case 'free':
          return fee.freePrice;
        default:
          return fee.basePrice * 0.75;
      }
    } else if (ticketType === 'senior') {
      switch (pricingTier) {
        case 'full':
          return fee.basePrice * 0.85;
        case 'reduced':
          return fee.reducedPrice * 0.85;
        case 'free':
          return fee.freePrice;
        default:
          return fee.basePrice * 0.85;
      }
    }

    switch (pricingTier) {
      case 'full':
        return fee.basePrice;
      case 'reduced':
        return fee.reducedPrice;
      case 'free':
        return fee.freePrice;
      default:
        return fee.basePrice;
    }
  };

  const calculateTotal = (fee: Fee, feeId: string): number => {
    const q = getTicketQuantities(feeId);
    return (
      q.student * getPrice(fee, 'student') +
      q.adult * getPrice(fee, 'adult') +
      q.senior * getPrice(fee, 'senior')
    );
  };

  const isInCart = (feeId: string) => {
    return cart.items.some(item => item.fee.id === feeId);
  };

  const handleAddToCart = (fee: Fee) => {
    const q = getTicketQuantities(fee.id);
    const totalQty = getTotalQuantity(fee.id);

    if (totalQty === 0) {
      return;
    }

    if (isInCart(fee.id)) {
      return;
    }

    setLoadingItems(prev => new Set(prev).add(fee.id));

    if (q.student > 0) {
      addToCart(fee, q.student, pricingTier, { ticketType: 'Student' }, undefined, undefined, getPrice(fee, 'student'));
    }
    if (q.adult > 0) {
      addToCart(fee, q.adult, pricingTier, { ticketType: 'Adult' }, undefined, undefined, getPrice(fee, 'adult'));
    }
    if (q.senior > 0) {
      addToCart(fee, q.senior, pricingTier, { ticketType: 'Senior' }, undefined, undefined, getPrice(fee, 'senior'));
    }

    setTimeout(() => {
      setLoadingItems(prev => {
        const next = new Set(prev);
        next.delete(fee.id);
        return next;
      });
    }, 500);
  };

  return (
    <>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #0891b2 100%)',
          color: 'white',
          py: { xs: 3, sm: 4, md: 5 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CalendarMonth sx={{ fontSize: { xs: 40, md: 50 } }} />
            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
              }}
            >
              Events
            </Typography>
          </Box>
          <Typography
            variant="h6"
            sx={{
              opacity: 0.95,
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
            }}
          >
            Purchase tickets for sporting events, concerts, and school activities
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 2, sm: 3 } }}>
        {!isAuthenticated && (
          <Paper
            sx={{
              p: 3,
              mb: 4,
              bgcolor: '#e0f2fe',
              border: '1px solid',
              borderColor: '#0891b2',
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ color: '#0e7490', fontWeight: 600 }}>
              Public Access
            </Typography>
            <Typography variant="body2" sx={{ color: '#0e7490' }}>
              You are browsing as a guest. You can purchase event tickets and will be asked to provide your email during checkout.
            </Typography>
          </Paper>
        )}

        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <FilterList />
            <Typography variant="h6" fontWeight={600}>
              Search & Filter Events
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Range"
                  onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                >
                  <MenuItem value="all">All Events</MenuItem>
                  <MenuItem value="thisWeek">This Week</MenuItem>
                  <MenuItem value="nextWeek">Next Week</MenuItem>
                  <MenuItem value="thisMonth">This Month</MenuItem>
                  <MenuItem value="nextMonth">Next Month</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Sport Type</InputLabel>
                <Select
                  value={sportFilter}
                  label="Sport Type"
                  onChange={(e) => setSportFilter(e.target.value as SportFilter)}
                >
                  <MenuItem value="all">All Sports</MenuItem>
                  <MenuItem value="basketball">Basketball</MenuItem>
                  <MenuItem value="football">Football</MenuItem>
                  <MenuItem value="baseball">Baseball</MenuItem>
                  <MenuItem value="swimming">Swimming</MenuItem>
                  <MenuItem value="other">Other Events</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {(searchQuery || dateFilter !== 'thisWeek' || sportFilter !== 'all') && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Active filters:
              </Typography>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  size="small"
                  onDelete={() => setSearchQuery('')}
                />
              )}
              {dateFilter !== 'thisWeek' && (
                <Chip
                  label={`Date: ${dateFilter === 'all' ? 'All' : dateFilter === 'nextWeek' ? 'Next Week' : dateFilter === 'thisMonth' ? 'This Month' : 'Next Month'}`}
                  size="small"
                  onDelete={() => setDateFilter('thisWeek')}
                />
              )}
              {sportFilter !== 'all' && (
                <Chip
                  label={`Sport: ${sportFilter.charAt(0).toUpperCase() + sportFilter.slice(1)}`}
                  size="small"
                  onDelete={() => setSportFilter('all')}
                />
              )}
            </Box>
          )}
        </Paper>

        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            {eventFees.length} {eventFees.length === 1 ? 'event' : 'events'} found
          </Typography>
        </Box>

        {eventFees.length === 0 ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Events Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchQuery || dateFilter !== 'thisWeek' || sportFilter !== 'all'
                ? 'Try adjusting your filters or search terms to find more events.'
                : 'Check back later for upcoming events and activities.'}
            </Typography>
            {(searchQuery || dateFilter !== 'thisWeek' || sportFilter !== 'all') && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setDateFilter('all');
                  setSportFilter('all');
                }}
              >
                Clear All Filters
              </Button>
            )}
          </Box>
        ) : (
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {eventFees.map(fee => {
              const q = getTicketQuantities(fee.id);
              const totalQty = getTotalQuantity(fee.id);
              const total = calculateTotal(fee, fee.id);

              const sportType = getSportType(fee.name);
              const sportColors = {
                basketball: { bg: '#fff3e0', color: '#e65100', border: '#ffb74d' },
                football: { bg: '#fce4ec', color: '#c2185b', border: '#f48fb1' },
                baseball: { bg: '#e8f5e9', color: '#2e7d32', border: '#81c784' },
                swimming: { bg: '#e1f5fe', color: '#0277bd', border: '#4fc3f7' },
                other: { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' },
              };
              const sportColor = sportColors[sportType];

              return (
                <Grid size={{ xs: 12, lg: 6 }} key={fee.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" fontWeight={600} sx={{ flex: 1, pr: 2, minWidth: '200px' }}>
                          {fee.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={sportType.charAt(0).toUpperCase() + sportType.slice(1)}
                            size="small"
                            sx={{
                              bgcolor: sportColor.bg,
                              color: sportColor.color,
                              fontWeight: 600,
                              border: `1px solid ${sportColor.border}`,
                            }}
                          />
                          <Chip
                            label={getSchoolYearName(fee.schoolYearIds[0])}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1565c0',
                              fontWeight: 600,
                              border: '1px solid #90caf9',
                            }}
                          />
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {fee.description}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
                        Select Ticket Quantities
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Student / Child
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                              ${getPrice(fee, 'student').toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'student', q.student - 1)}
                                disabled={q.student <= 0}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                value={q.student}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  updateTicketQuantity(fee.id, 'student', val);
                                }}
                                inputProps={{
                                  style: { textAlign: 'center', padding: '6px 8px' },
                                  min: 0,
                                }}
                                sx={{ width: '60px' }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'student', q.student + 1)}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Adult
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                              ${getPrice(fee, 'adult').toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'adult', q.adult - 1)}
                                disabled={q.adult <= 0}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                value={q.adult}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  updateTicketQuantity(fee.id, 'adult', val);
                                }}
                                inputProps={{
                                  style: { textAlign: 'center', padding: '6px 8px' },
                                  min: 0,
                                }}
                                sx={{ width: '60px' }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'adult', q.adult + 1)}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Senior (65+)
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight={600} gutterBottom>
                              ${getPrice(fee, 'senior').toFixed(2)}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'senior', q.senior - 1)}
                                disabled={q.senior <= 0}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Remove fontSize="small" />
                              </IconButton>
                              <TextField
                                size="small"
                                value={q.senior}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 0;
                                  updateTicketQuantity(fee.id, 'senior', val);
                                }}
                                inputProps={{
                                  style: { textAlign: 'center', padding: '6px 8px' },
                                  min: 0,
                                }}
                                sx={{ width: '60px' }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => updateTicketQuantity(fee.id, 'senior', q.senior + 1)}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <Add fontSize="small" />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Total Tickets
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {totalQty}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Price
                          </Typography>
                          <Typography variant="h5" color="primary" fontWeight={600}>
                            ${total.toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>

                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={
                          loadingItems.has(fee.id) ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : isInCart(fee.id) ? (
                            <Check />
                          ) : (
                            <ShoppingCart />
                          )
                        }
                        onClick={() => handleAddToCart(fee)}
                        disabled={loadingItems.has(fee.id) || isInCart(fee.id) || totalQty === 0}
                      >
                        {loadingItems.has(fee.id)
                          ? 'Adding...'
                          : isInCart(fee.id)
                          ? 'In Cart'
                          : totalQty === 0
                          ? 'Select Tickets'
                          : 'Add to Cart'}
                      </Button>

                      {isInCart(fee.id) && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: '#f0fdf4', borderRadius: 1, border: '1px solid #86efac' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Wallet sx={{ fontSize: 20, color: '#15803d' }} />
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#15803d' }}>
                              Add to Wallet
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#15803d' }}>
                            After checkout, you'll receive your event tickets with options to add them to Apple Wallet or Google Pay.
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </>
  );
};
